export type UnexpectedError =
  | { success?: false; value?: any }
  | { success?: false; [key: string]: any };
export type ErrorResult<TError> = { success?: false; expectedError: TError } | UnexpectedError;
export type Result<TResult, TError> = { success: true; value: TResult } | ErrorResult<TError>;
export type ResultWithoutExpectedErrors<TResult> =
  | { success: true; value: TResult }
  | UnexpectedError;
export type EmptyResult<TError> = { success: true } | ErrorResult<TError>;
// It does not work:
// type EmptyResult = { success: true } | UnexpectedError;
// Duplicate identifier 'EmptyResult'.ts(2300)
// TODO: Research how to fix it and rename EmptyResultWithoutExpectedErrors as EmptyResult
export type EmptyResultWithoutExpectedErrors = { success: true } | UnexpectedError;

export type PathType = 'free' | 'standard' | 'plus' | 'agencies';

export const SUBSCRIBERS_LIMIT_EXCLUSIVE_DISCOUNT_ARGENTINA = 10000;
export const EXCLUSIVE_DISCOUNT_PERCENTAGE_ARGENTINA = 20;

export const PLAN_TYPE = {
  free: 'free',
  byCredit: 'prepaid',
  byEmail: 'monthly-deliveries',
  byContact: 'subscribers',
  agencies: 'agencies',
} as const;

export const COLLABORATOR_SECTION = {
  Reports: 1,
  Campaigns: 2,
  Lists: 3,
  ControlPanel: 4,
  DownloadCenter: 5,
  Steps: 8,
  Automation: 10,
  Integration: 11,
  Templates: 12,
  Approver: 13,
  Dashboard: 14,
  Conversations: 15,
  PopUpHub: 16,
  Landings: 18,
} as const;

export const URL_PLAN_TYPE = {
  [PLAN_TYPE.byContact]: 'by-contacts',
  [PLAN_TYPE.byEmail]: 'by-emails',
  [PLAN_TYPE.byCredit]: 'by-credits',
} as const;

export const SUBSCRIPTION_TYPE = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  biyearly: 'half-yearly',
  yearly: 'yearly',
} as const;

export const MAX_LANDING_PACKAGE = 10;
export const MAX_ECOAI_PACKAGE = 1;

export type PlanType = typeof PLAN_TYPE[keyof typeof PLAN_TYPE];

export type PaymentType = 'CC' | 'transfer';

export type BillingCycle = 'monthly' | 'quarterly' | 'half-yearly' | 'yearly';

export type Path = FreePath | StandardPath | PlusPath | AgenciesPath;

export interface AdvancePayOptions {
  id: number;
  paymentType: 'CC' | 'transfer';
  discountPercentage: number;
  billingCycle: BillingCycle;
  applyPromo: boolean;
}

export type Features =
  | 'emailParameter'
  | 'cancelCampaign'
  | 'siteTracking'
  | 'smartCampaigns'
  | 'shippingLimit';

export interface ContactPlan {
  type: 'subscribers';
  id: number;
  name: string;
  subscriberLimit: number;
  subscribersQty?: number;
  fee: number;
  billingCycleDetails: AdvancePayOptions[];
  currentSubscription: number;
}

export interface EmailPlan {
  type: 'monthly-deliveries';
  id: number;
  name: string;
  emailsByMonth: number;
  emailQty?: number;
  extraEmailPrice: number;
  fee: number;
}

export interface CreditPlan {
  type: 'prepaid';
  id: number;
  name: string;
  credits: number;
  emailQty?: number;
  price: number;
  subscribersCount: number;
}

export interface FreePlan {
  type: 'free';
  subscriberLimit: number;
}

export interface AgenciesPlan {
  type: 'agencies';
}

export interface FreePath {
  type: 'free';
  current: boolean;
}

export type Plan = ContactPlan | FreePlan | CreditPlan | EmailPlan | AgenciesPlan;

export type FeaturedPlan = ContactPlan | EmailPlan;

export interface StandardPath {
  type: 'standard';
  current: boolean;
  minimumFee: number;
}

export interface PlusPath {
  type: 'plus';
  current: boolean;
  minimumFee: number;
}

export interface AgenciesPath {
  type: 'agencies';
  current: boolean;
}

export interface AppStatus {
  offline: boolean;
}

export type IntegrationStatus = 'connected' | 'disconnected' | 'alert';

export const PaymentMethodType = {
  creditCard: 'CC',
  mercadoPago: 'MP',
  transfer: 'TRANSF',
  automaticDebit: 'DA',
};

export const FirstDataError = {
  invalidExpirationDate: 'DeclinedPaymentTransaction - Invalid Expiration Date [Bank]',
  invalidCreditCardNumber: 'DeclinedPaymentTransaction - Invalid Credit Card Number',
  invalidCCNumber: 'DeclinedPaymentTransaction - Invalid CC Number [Bank]',
  declined: 'DeclinedPaymentTransaction - Declined [Bank]',
  doNotHonorDeclined: 'DoNotHonorPaymentResponse - Processor Decline [Bank]',
  suspectedFraud: 'DeclinedPaymentTransaction - Suspected Fraud [Bank]',
  insufficientFunds: 'DeclinedPaymentTransaction - Insufficient Funds [Bank]',
  cardVolumeExceeded: 'DeclinedPaymentTransaction - Card Volume Exceeded',
  doNotHonor: 'DoNotHonorPaymentResponse - Do Not Honor [Bank]',
};

export const MercadoPagoError = {
  invalidExpirationDate: 'DeclinedPaymentTransaction - cc_rejected_bad_filled_date',
  invalidSecurityCode: 'DeclinedPaymentTransaction - cc_rejected_bad_filled_security_code',
  insufficientFunds: 'DeclinedPaymentTransaction - cc_rejected_insufficient_amount',
  declinedOtherReason: 'DeclinedPaymentTransaction - cc_rejected_other_reason',
  suspectedFraud: 'DeclinedPaymentTransaction - cc_rejected_high_risk',
  pending: 'Pending',
};

export const CloverError = {
  declined: 'DeclinedPaymentTransaction - card_declined',
  invalidCreditCardNumber: 'DeclinedPaymentTransaction - invalid_number',
  invalidExpirationMonth: 'DeclinedPaymentTransaction - invalid_expiry_month',
  invalidExpirationYear: 'DeclinedPaymentTransaction - invalid_expiry_year',
  invalidSecurityCode: 'DeclinedPaymentTransaction - incorrect_cvc"',
  insufficientFunds: 'DeclinedPaymentTransaction - invalid_tip_amount',
  invalidExpirationCard: 'DeclinedPaymentTransaction - expired_card',
};

export const AutomaticDebitError = {
  cbuInvalid: 'CbuInvalid',
};

export const EProtectError = {
  invalidAccountNumber: '871',
  accountNumberTooShort: '872',
  accountNumberTooLong: '873',
  accountNumberNotNumeric: '874',
  unableToEncryptField: '875',
  invalidAccountNumberGeneric: '876',
  invalidPaypageRegistrationId: '877',
  expiredPaypageRegistrationId: '878',
  merchantNotAuthorized: '879',
  reportGroupInvalid: '880',
  cvvNotNumeric: '881',
  cvvTooShort: '882',
  cvvTooLong: '883',
  payframeHtmlFailedToLoad: '884',
  payframeCssFailedToLoad: '885',
  expirationMonthInvalid: '886-month',
  expirationYearInvalid: '886-year',
  expirationDateInvalid: '886',
  secondaryPaypageRequestError: '887',
  paypageSignatureVerificationFailed: '888',
  failure: '889',
  pinNumMissingOrTooShort: '893',
  pinTooLong: '894',
  genericTokenRegistrationError: '898',
};

export const RaftApiError = {
  declined: 'DeclinedPaymentTransaction - 016',
  declinedFraud: 'DeclinedPaymentTransaction - 017',
  doNotHonor: 'DeclinedPaymentTransaction - 005',
  doNotHonorFraud: 'DeclinedPaymentTransaction - 025',
  invalidCreditCardNumber: 'DeclinedPaymentTransaction - 018',
  invalidExpirationMonth: 'DeclinedPaymentTransaction - 045',
  invalidExpirationYear: 'DeclinedPaymentTransaction - 045',
  invalidSecurityCode: 'DeclinedPaymentTransaction - 013',
  insufficientFunds: 'DeclinedPaymentTransaction - 039',
  invalidExpirationCard: 'DeclinedPaymentTransaction - 004',
  invalidToken: 'DeclinedPaymentTransaction - 078',
};

export const OnlySupportUpSelling = 'Invalid selected plan. Only supports upselling.';

export const nonAuthenticatedBlockedUser = 'non-authenticated-blocked-user';

export const numberFormatOptions = {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: true,
};

export const paymentFrequenciesListFake = [
  {
    numberMonths: 12,
    subscriptionType: 'yearly',
    discountPercentage: 25,
  },
  {
    numberMonths: 6,
    subscriptionType: 'half_yearly',
    discountPercentage: 15,
  },
  {
    numberMonths: 3,
    subscriptionType: 'quarterly',
    discountPercentage: 5,
  },
  {
    numberMonths: 1,
    subscriptionType: 'monthly',
    discountPercentage: 0,
  },
];

export const BUY_MARKETING_PLAN = 1;
export const BUY_LANDING_PACK = 3;
export const BUY_CHAT_PLAN = 2;
export const BUY_ONSITE_PLAN = 4;
export const BUY_PUSH_NOTIFICATION_PLAN = 5;
export const BUY_ECO_IA_PLAN = 6;

export const AddOnType = {
  Conversations: 2,
  Landings: 1,
  OnSite: 3,
  PushNotifications: 4,
  EcoAI: 5,
};

export const AccountCancellationFlow = {
  free: 1,
  greaterOrEqual1000ContactsOrMonthly: 2,
  lessOrEqual5000ContactsOrCredits: 3,
};

export interface PushNotificationSettings {
  consumedSends: number;
  trialPeriodRemainingDays: number;
  isPushServiceEnabled: boolean;
  hasPushTrialPlan: boolean;
  hasDomainsWithPushActive: boolean;
};

export interface PushNotificationSettingsToUpdate {
  isPushServiceEnabled: boolean;
};

export const ConversationsEnvSource = {
  Doppler: 'DOPPLER',
  Beplic: 'BEPLIC',
} as const;
